import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'

import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import MeetingPlaceCard from '../components/MeetingPlaceCard'
import type { AvailabilityWizardValues } from '../CreateAvailabilityWizard'

export default function MeetingPlaceStep() {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext<AvailabilityWizardValues>()

  const meetingPlaceTestId: Record<AvailabilityMeetingPlace, string> = {
    [AvailabilityMeetingPlace.CENTER]: 'availability-wizard-place-center',
    [AvailabilityMeetingPlace.AT_HOME]: 'availability-wizard-place-at-home',
    [AvailabilityMeetingPlace.VISIO_CONFERENCE]: 'availability-wizard-place-visio',
    [AvailabilityMeetingPlace.SECOND_OPINION]: 'availability-wizard-place-second-opinion',
  }

  const meetingPlaces = [
    {
      value: AvailabilityMeetingPlace.CENTER,
      label: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.center'),
      description: t('page.meetingLink.wizard.meetingPlace.center.description', 'Patients come to your practice'),
      icon: <BusinessOutlinedIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      isRecommended: true,
    },
    {
      value: AvailabilityMeetingPlace.AT_HOME,
      label: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.atHome'),
      description: t('page.meetingLink.wizard.meetingPlace.atHome.description', 'You travel to the patient'),
      icon: <HomeOutlinedIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    },
    {
      value: AvailabilityMeetingPlace.VISIO_CONFERENCE,
      label: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.visioConference'),
      description: t('page.meetingLink.wizard.meetingPlace.visio.description', 'Meet patients remotely'),
      icon: <VideocamOutlinedIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    },
    {
      value: AvailabilityMeetingPlace.SECOND_OPINION,
      label: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.secondOpinion'),
      description: t('page.meetingLink.wizard.meetingPlace.secondOpinion.description', 'Review files, no meeting'),
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    },
  ]

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h6">
          {t('page.meetingLink.wizard.meetingPlace.title', 'How will you meet patients?')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('page.meetingLink.wizard.meetingPlace.subtitle', 'This determines what information we need from you')}
        </Typography>
      </Stack>

      <Box
        role="radiogroup"
        aria-label={t('page.meetingLink.wizard.meetingPlace.ariaLabel', 'Meeting place selection') as string}
        sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}
      >
        {meetingPlaces.map((place) => (
          <MeetingPlaceCard
            key={place.value}
            dataTestId={meetingPlaceTestId[place.value]}
            description={place.description}
            icon={place.icon}
            isRecommended={place.isRecommended}
            recommendedLabel={t('page.meetingLink.wizard.meetingPlace.recommended', 'Recommended')}
            label={place.label}
            onSelect={() => setFieldValue('meetingPlace', place.value)}
            selected={values.meetingPlace === place.value}
          />
        ))}
      </Box>

      <Alert severity="info" variant="outlined">
        {t('page.meetingLink.wizard.meetingPlace.hint', 'Start with the one you use most. Add others anytime.')}
      </Alert>
    </Stack>
  )
}
